import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import graphqlService from '../services/graphql';
import { categoriesTrans } from '../constants';

const SEARCH_PAGE_SIZE = 24; // 搜索时每页加载的数量

// 原生防抖函数
function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

export function useProductListData() {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // 新增：首次加载状态
  const [showSkeleton, setShowSkeleton] = useState(false); // 新增骨架屏显示状态
  const [equipments, setEquipments] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [currentFilters, setCurrentFilters] = useState(null);
  
  // 使用 useRef 存储状态引用，避免依赖项问题
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const currentFiltersRef = useRef(null);
  const offsetRef = useRef(0);
  
  // 同步状态到 ref
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);
  
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  
  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters]);
  
  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);
  
  // 搜索和过滤状态
  const [searchText, setSearchText] = useState('');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 10000]);

  // 使用普通搜索方法加载数据
  const loadSearchData = useCallback(async (filters, currentOffset = 0, isLoadMore = false) => {
    setLoading(true);
    
    try {
      let skeletonTimer = null;
      
      // 只在首次加载且没有数据时设置骨架屏延迟显示
      if (!isLoadMore && currentOffset === 0 && initialLoading) {
        // 设置延迟显示骨架屏的定时器
        skeletonTimer = setTimeout(() => {
          setShowSkeleton(true); // 300ms后才显示骨架屏
        }, 300);
      }
      
      // 转换分类标签为值
      const categoryValues = filters.categories.length > 0 
        ? filters.categories.map(label => {
            const categoryItem = categoriesTrans.find(c => c.label === label);
            return categoryItem ? categoryItem.value : label;
          })
        : [];

      // 构建搜索参数
      const searchParams = {
        searchText: filters.searchText || null,
        brands: filters.brands.length > 0 ? filters.brands : null,
        categories: categoryValues.length > 0 ? categoryValues : null,
        styles: filters.styles.length > 0 ? filters.styles : null,
        minPrice: filters.minPrice !== null ? filters.minPrice : null,
        maxPrice: filters.maxPrice < 10000 ? filters.maxPrice : null
      };

      // 调用后端搜索API（普通模式使用新的分页方法）
      const results = await graphqlService.searchEquipments(
        searchParams,
        currentOffset,
        SEARCH_PAGE_SIZE
      );

      // 清除骨架屏定时器
      if (skeletonTimer) {
        clearTimeout(skeletonTimer);
      }
      
      // 隐藏骨架屏
      setShowSkeleton(false);

      if (isLoadMore) {
        setEquipments(prev => {
          // 避免重复添加
          const existingIds = new Set(prev.map(e => e.id));
          const newEquipments = results.filter(e => !existingIds.has(e.id));
          return [...prev, ...newEquipments];
        });
        setOffset(prev => prev + SEARCH_PAGE_SIZE);
      } else {
        setEquipments(results);
        setOffset(SEARCH_PAGE_SIZE);
      }
      
      // 判断是否还有更多数据
      setHasMore(results.length === SEARCH_PAGE_SIZE);
      
      // 首次加载完成
      if (initialLoading) {
        setInitialLoading(false);
      }
      
    } catch (error) {
      console.error('Error loading search data:', error);
      if (!isLoadMore) {
        setEquipments([]);
        setHasMore(false);
      }
      // 隐藏骨架屏并标记首次加载完成
      setShowSkeleton(false);
      if (initialLoading) {
        setInitialLoading(false);
      }
    } finally {
      setLoading(false);
    }
  }, [initialLoading]); // 移除equipments.length依赖

  // 构建过滤条件
  const filters = useMemo(() => ({
    searchText: searchText.trim(),
    brands: selectedBrands,
    categories: selectedCategories,
    styles: selectedStyles,
    minPrice: priceRange[0],
    maxPrice: priceRange[1]
  }), [searchText, selectedBrands, selectedCategories, selectedStyles, priceRange]);

  // 防抖搜索函数
  const debouncedSearch = useCallback(
    debounce((searchFilters) => {
      setCurrentFilters(searchFilters);
      loadSearchData(searchFilters, 0, false);
    }, 300),
    [loadSearchData]
  );

  // 当过滤条件改变时，重新搜索
  useEffect(() => {
    // 重置状态并开始搜索（无论是否有过滤条件）
    setOffset(0);
    setHasMore(true);
    debouncedSearch(filters);
  }, [filters, debouncedSearch]);

  // 加载更多数据
  const loadMore = useCallback(() => {
    // 使用 ref 获取最新状态，避免依赖项问题
    if (loadingRef.current || !hasMoreRef.current || !currentFiltersRef.current) {
      return;
    }
    
    loadSearchData(currentFiltersRef.current, offsetRef.current, true);
  }, [loadSearchData]); // 只依赖 loadSearchData

  return {
    // 数据状态
    loading,
    initialLoading,
    showSkeleton, // 新增骨架屏显示状态
    equipments,
    loadMore,
    hasMore,
    
    // 搜索和过滤状态
    searchText,
    setSearchText,
    selectedBrands,
    setSelectedBrands,
    selectedCategories,
    setSelectedCategories,
    selectedStyles,
    setSelectedStyles,
    priceRange,
    setPriceRange
  };
}